import { PriceGroupKey } from '@utils/selectPriceByGroup';

/**
 * Algolia exposes a sale-aware `effectivePrice` per pricing context, nested
 * under `prices.<groupKey>` (the tier's sale price when on sale, otherwise its
 * regular price). Sort and filter ranking use these per-context values so each
 * customer sorts/filters on the price they actually pay.
 */
export const getPriceAttribute = (groupKey: PriceGroupKey): string =>
  `prices.${groupKey}.effectivePrice`;

/**
 * Replica suffix for price sort. Guest keeps the original `_price_asc`/
 * `_price_desc` replica names; every other tier gets a `_price_<tier>_*`
 * replica (e.g. `_price_member_asc`).
 */
export const getPriceSortSuffix = (groupKey: PriceGroupKey): string =>
  groupKey === 'guest' ? '' : `_${groupKey}`;

/**
 * True for any nested per-context effective price attribute
 * (e.g. `prices.guest.effectivePrice`, `prices.member.effectivePrice`).
 */
export const isEffectivePriceAttribute = (attribute: string): boolean =>
  attribute.startsWith('prices.') && attribute.endsWith('.effectivePrice');

/**
 * Move a range refinement from one price-context key to another, preserving its
 * value. The router parses the URL once at InstantSearch init, while the
 * customer context is still unhydrated (`guest`), so a reloaded price range
 * lands under the guest attribute. When the context later hydrates to another
 * tier, the price widgets switch attribute and would find no range under the
 * new key; migrating the value keeps the refinement applied.
 *
 * Returns the original state unchanged when the keys match or there is nothing
 * to migrate, so it is safe to use as an InstantSearch `setIndexUiState`
 * reducer (no spurious state updates).
 */
export const migratePriceRange = <T extends { range?: Record<string, string> }>(
  uiState: T,
  fromAttribute: string,
  toAttribute: string
): T => {
  if (fromAttribute === toAttribute) return uiState;

  const value = uiState.range?.[fromAttribute];
  if (value === undefined) return uiState;

  const nextRange = { ...uiState.range, [toAttribute]: value };
  delete nextRange[fromAttribute];

  return { ...uiState, range: nextRange };
};
